/* eslat-disable array-callback-return */
// REACT IMPORTS
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Drawer from "components/ui-component/SideDrawer";
import AddressComp from "components/payments/AddressComp";

// COMPONENT IMPORTS
import FailedPayment from "./FailedPayment";
import { paymentLogoGet, paymentReceiptGet } from "store/slices/paymentsSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import Skeleton from "react-loading-skeleton";
import { useUserData } from "store/slices/authSlice";
import {
  changePaymentScheduleName,
  encode,
  formatCurrency,
  getPaymentMethod,
} from "utils/HelperFunctions";

// ICONS IMPORTS
import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { FilledInfoIcon } from "icons/payments/FilledInfoIcon";
import QuestionsFooter from "components/auth/QuestionsFooter";
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { RedCrossIcon } from "icons/payments/RedCrossIcon";
import { MoneyReciveIcon } from "icons/payments/MoneyReciveIcon";
import { DollarProcessIcon } from "icons/payments/DollarProcessIcon";
import { ClockYellowIcon } from "icons/payments/ClockYellowIcon";
import { CloseYellowIcon } from "icons/payments/CloseYellowIcon";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { TickSquare } from "icons/payments/TickSquare";
import { DangerIcon } from "icons/payments/DangerIcon";
import { formatPaymentListDate } from "utils/DateFormat";
import Button from "components/form/Button";
import { decode } from "utils/HelperFunctions";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import {
  setIsDashPreview,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import { RootState } from "store/store";
import ExcalimIcon from "icons/payments/ExclaimIcon";
import ExclaimIcon2 from "icons/payments/ExclaimIcon2";
import { useTid } from "utils/testid";
import {
  getScheduledPaymentDownloadOrderId,
  PaymentOrderId,
} from "utils/paymentPreviewDownload";

// PROPS TYPE
interface PaymentPreviewProps {
  id: string;
  setId: any;
  show: boolean;
  setShow: any;
  onDownloadCLick?: any;
  isScheduled?: boolean | false;
  isDownloading?: any | "";
  custom_schedule_id?: string | number | null;
}
type Stats = {
  [key: string]: string[];
};

type ClientScheduleCustomData = {
  id?: string | number;
  order_id?: PaymentOrderId | null;
  billing_date?: string | null;
  status?: string | null;
};

/* ------------------------------------- PAYMENT PREVIEW ------------------------------------- */

const PaymentPreview = (props: PaymentPreviewProps) => {
  const tid = useTid();
  const {
    id,
    setId,
    show,
    setShow,
    onDownloadCLick,
    isScheduled,
    isDownloading,
    custom_schedule_id,
  } = props;
  const isLessThan680 = useMediaQuery({ query: "(max-width:680px)" });
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  const dispatch = useAppDispatch();
  const { paymentLogo } = useAppSelector((state: RootState) => state.payment);
  const portalSettings = usePortalSettingsData();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<any>({});
  const [receiptId, setReceiptId] = useState<string>("");
  const [cppData, setCppData] = useState<any>({});
  const [scheduledDetails, setScheduledDetails] = useState<any>({});
  const [scheduled, setScheduled] = useState<any>([]);
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const [failedPayment, setFailedPayment] = useState<boolean>(false);
  const [failedPaymentId, setFailedPaymentId] = useState<string | null>(null);
  const [selectedScheduleData, setSelectedScheduleData] =
    useState<ClientScheduleCustomData | null>(null);

  useEffect(() => {
    // Always hand the flag back when this preview goes away or the screen
    // grows past the mobile breakpoint. Without that, navigating away with
    // the preview open left it stuck on, and the layout kept the mobile
    // header and footer hidden on every page.
    if (!isLessThan590) {
      dispatch(setIsDashPreview?.(false));
      return;
    }
    dispatch(setIsDashPreview?.(show));
    return () => {
      dispatch(setIsDashPreview?.(false));
    };
  }, [show, isLessThan590]);

  const userData = useUserData();
  const { clusterId: cluster_id, id: actionId } = useParams();
  const clusterId = cluster_id
    ? decode(cluster_id || "", true)
    : actionId
      ? decode(actionId || "")?.split("_")[1]
      : userData?.data?.accountData?.clusterid;

  const PaymentStatus: Stats = {
    "0": ["Failed", "Disputed", "Disputed Lost"],
    "1": ["Succeeded", "Disputed Won"],
    "2": ["Refunded", "Partial refund"],
    "3": ["Voided"],
    "4": ["Voided"],
    "5": ["Voided"],
    "7": ["Pending"],
    "8": ["Incomplete"],
  };
  const getStatusData = (
    payment_gateway_resp: string,
    transaction_status: string
  ): string => {
    if (payment_gateway_resp) {
      let stat = PaymentStatus[payment_gateway_resp];
      if (stat.length === 1) {
        return stat[0];
      } else {
        if (payment_gateway_resp === "0") {
          return transaction_status === "Disputed"
            ? "Disputed"
            : transaction_status === "DisputedLoss"
              ? "Disputed Lost"
              : "Failed";
        } else if (payment_gateway_resp === "1") {
          return transaction_status === "DisputedWon"
            ? "Disputed Won"
            : transaction_status === "Disputed"
              ? "Disputed"
              : "Succeeded";
        } else {
          return transaction_status === "RefundedPartial"
            ? "Partial refund"
            : "Refunded";
        }
      }
    }
    return "";
  };

  const onGetPaymentReceipt = async () => {
    setIsLoading(true);
    setSelectedScheduleData(null);
    try {
      let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
      let res: any = await dispatch(
        paymentReceiptGet({
          id: id,
          pid,
          source: isScheduled ? "clients_schedule_custom" : "dc_orders",
        })
      ).unwrap();
      if (res && res?.body?.data) {
        const clientsScheduleCustomData: ClientScheduleCustomData[] =
          Array.isArray(res.body.data.clientsScheduleCustomData)
            ? res.body.data.clientsScheduleCustomData
            : [];
        const selectedScheduleRecord =
          isScheduled && custom_schedule_id != null
            ? clientsScheduleCustomData.find(
                (item) => String(item.id) === String(custom_schedule_id)
              )
            : undefined;

        setSelectedScheduleData(selectedScheduleRecord ?? null);

        if (res?.body?.data?.paymentData) {
          let data = { ...res?.body?.data?.paymentData };
          setReceiptId(res?.body?.data?.receiptId);
          setOrderData({
            ...data,
            billing_address1: data?.street,
            pay_amount: data?.amount,
            c_FeeAmount:
              data?.payment_method === "CC"
                ? data?.cc_fee_amount
                  ? data?.cc_fee_amount
                  : null
                : data?.ach_fee_amount
                  ? data?.ach_fee_amount
                  : null,
            grandTotal:
              data?.amount +
              (data?.payment_method === "CC"
                ? data?.cc_fee_amount
                  ? data?.cc_fee_amount
                  : 0
                : data?.ach_fee_amount
                  ? data?.ach_fee_amount
                  : 0),
          });

          setCppData({ ...res?.body?.data?.cppData });
          if (data?.payment_gateway_response) {
            setPaymentStatus(
              getStatusData(
                data.payment_gateway_response,
                data?.transaction_status
              )
            );
          }
        } else {
          let data = {
            ...res?.body?.data?.orderData,
            pay_amount: res?.body?.data?.amount
              ? parseFloat(res?.body?.data?.amount)
              : 0,
            grandTotal: res?.body?.data?.grandTotal
              ? parseFloat(res?.body?.data?.grandTotal)
              : 0,
            c_FeeAmount: res?.body?.data?.totalconveFeeAmount
              ? parseFloat(res?.body?.data?.totalconveFeeAmount)
              : null,
          };
          let payment_date = res?.body?.data?.orderData?.insert_date;
          if (selectedScheduleRecord) {
            payment_date = selectedScheduleRecord.billing_date;
          }

          let scheduledData = { ...res?.body?.data?.getCustomerData };
          let scheduledArr = clientsScheduleCustomData.filter(
            (item) => item.status === "Pending"
          );
          setReceiptId(res?.body?.data?.receiptId);
          setCppData({ ...res?.body?.data?.cppData });
          setOrderData({
            ...data,
            insert_date: payment_date,
          });
          if (data?.payment_gateway_response) {
            if (
              (data?.payment_method === "WTO" ||
                data?.payment_method === "WT") &&
              data.ivr_type != "MANUALPAY" &&
              scheduledArr?.[0]?.status === "Pending"
            ) {
              setPaymentStatus("Pending");
            } else {
              setPaymentStatus(
                getStatusData(
                  data.payment_gateway_response,
                  data?.transaction_status
                )
              );
            }
          }
          setScheduledDetails({
            ...scheduledData,
          });
          setScheduled([...scheduledArr]);
        }
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("Get payment receipt err: -->", error?.message);
    }
  };

  const onGetLogo = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await dispatch(paymentLogoGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get logo err: -->", error?.message);
    }
  };

  useEffect(() => {
    if (!paymentLogo && clusterId && show) onGetLogo();
  }, [paymentLogo, clusterId]);

  useEffect(() => {
    if (id && show) {
      onGetPaymentReceipt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, show]);

  useEffect(() => {
    if (!show) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onClose = () => {
    setId("");
    setShow(false);
    setScheduled([]);
    setScheduledDetails({});
    setOrderData({});
    setCppData({});
    setPaymentStatus("");
    setSelectedScheduleData(null);
  };

  const isScheduledArray = useMemo(() => {
    if (orderData?.payment_method === "WTO") {
      if (paymentStatus !== "Succeeded" && !isLoading && scheduled?.length) {
        return true;
      } else {
        return false;
      }
    } else {
      if (scheduled?.length && !isLoading) {
        return true;
      } else {
        return false;
      }
    }
  }, [orderData, paymentStatus, scheduled, isLoading]);

  const scheduledTotals = useMemo(
    () =>
      scheduled.reduce(
        (totals: { amount: number; fee: number }, item: any) => ({
          amount: totals.amount + (Number(item?.amount) || 0),
          fee: totals.fee + (Number(item?.cf_amount) || 0),
        }),
        { amount: 0, fee: 0 }
      ),
    [scheduled]
  );

  const scheduledDownloadOrderId = getScheduledPaymentDownloadOrderId(
    orderData?.payment_method,
    selectedScheduleData?.order_id
  );

  return (
    <div
      className="bg-bgSecondary h-[var(--shell-vh)] overflow-y-auto pt-[70px]"
      data-testid={tid("payment-preview")}
      aria-label="Payment receipt preview"
    >
      <div
        className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-30"
        data-testid={tid("payment-preview-header")}
      >
        <div className="flex items-center gap-2">
          <EyeIcon
            aria-hidden="true"
            strokeColor="var(--color-txt-primary)"
            width="24"
            height="24"
          />
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            Preview
          </div>
        </div>
        <div className="flex items-center gap-5">
          {(!isScheduled || scheduledDownloadOrderId !== null) && (
            <div
              role="button"
              tabIndex={0}
              aria-label={`Download payment receipt ${
                receiptId ||
                (isScheduled ? scheduledDownloadOrderId : orderData.id) ||
                ""
              }`}
              data-testid={tid("payment-preview-download-button")}
              className="flex items-center gap-2 cursor-pointer"
              onClick={() =>
                onDownloadCLick(
                  isScheduled ? scheduledDownloadOrderId : orderData.id,
                  receiptId,
                  paymentStatus === "Pending" &&
                    orderData.payment_method === "WTO"
                )
              }
            >
              {isDownloading ? (
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              ) : (
                <DownloadIcon
                  aria-hidden="true"
                  strokeColor="var(--color-btn-primary-active)"
                  width="16"
                  height="16"
                />
              )}
              <div className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline">
                {isDownloading ? "Downloading..." : "Download"}
              </div>
            </div>
          )}
          <div
            role="button"
            tabIndex={0}
            aria-label="Close payment preview"
            data-testid={tid("payment-preview-close-button")}
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon />
          </div>
        </div>
      </div>
      <div className="lg:px-10 px-5 py-4 flex items-center justify-between">
        <div className="md:text-xl text-lg font-normal leading-normal text-txtPrimary text-left w-full">
          {orderData &&
            (orderData?.payment_method === "WTO" &&
            orderData?.ivr_type !== "MANUALPAY" &&
            (paymentStatus === "Pending" || isScheduled) ? (
              "Wire Transfer Pending"
            ) : isLoading ? (
              <Skeleton width={100} height={35} />
            ) : (
              "Payment Receipt"
            ))}
        </div>
        <div className="flex items-center gap-[10px] text-right">
          {isLoading ? (
            <Skeleton width={100} height={35} />
          ) : paymentLogo ? (
            <div className="h-16 lg:h-16 flex justify-end w-fit">
              <img
                src={paymentLogo}
                alt="Payment method logo"
                className="animate__animated animate__fadeIn object-contain"
              />
            </div>
          ) : (
            <img
              src={logo}
              alt="DCDial"
              className="animate__animated animate__fadeIn"
            />
          )}
          {!paymentLogo && (
            <div className="text-purple font-medium text-xl leading-[30px]">
              CompanyName
            </div>
          )}
        </div>
      </div>
      <div className="lg:px-10 px-5 pb-2">
        <div className="lg:px-6 px-4 py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
          <div className="flex flex-col items-start gap-5">
            <div className="text-txtPrimary text-sm leading-normal font-bold uppercase">
              Billed to
            </div>
            <div className="h-[1px] w-full bg-strokePrimary" />
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="text-txtSecondary font-normal text-sm leading-normal">
                  Account ID
                </div>
                <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? <Skeleton width={80} /> : orderData?.accountId}
                </div>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary">
                  Customer
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={150} />
                  ) : (
                    `${orderData?.first_name || ""} ${
                      orderData?.last_name || ""
                    }`
                  )}
                </div>
              </div>
            </div>

            <AddressComp
              orderData={orderData}
              cppData={cppData}
              isLoading={isLoading}
              hideForWireTransfer
            />

            {/* <div className="h-[1px] w-full bg-strokePrimary" /> */}
          </div>
        </div>
        <div className="lg:px-6 px-4 pb-5 bg-bgPrimary border-x border-b border-strokePrimary rounded-b-lg">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
              Payment method
            </div>
            <div className="flex items-center gap-2 text-txtPrimary text-right">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                <>
                  {orderData?.payment_method
                    ? getPaymentMethod({
                        card_type: orderData?.card_type,
                        method: orderData?.payment_method,
                        digits: orderData?.card_last_4_digits,
                        account_number: orderData?.card_last_4_digits,
                        className: "",
                        textClassName: "!text-txtPrimary",
                        isHidden: true,
                        ivr_type: orderData?.ivr_type,
                        bank_name: orderData?.bank_name,
                        rowId:id,
                      })
                    : "--"}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="lg:px-10 px-5 py-2 overflow-x-scroll">
        {orderData?.payment_method === "WTO" &&
        orderData?.ivr_type !== "MANUALPAY" &&
        (paymentStatus === "Pending" || isScheduled) ? (
          <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-t-lg">
            <div className="flex flex-col items-start gap-5">
              <div className="w-full flex justify-between items-center">
                <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                  Wire instructions
                </div>
                {isLoading ? (
                  <Skeleton width={120} height={20} />
                ) : !isScheduled ? (
                  <div className="w-fit ">
                    <span className="py-[3px] px-[10px] rounded-[6px] flex gap-[6px] items-center capitalize text-sm text-yellow bg-[#FDB6000C]">
                      <ClockYellowIcon /> {paymentStatus}
                    </span>
                  </div>
                ) : (
                  <div className="w-fit ">
                    <span className="py-[3px] px-[10px] rounded-[6px] flex gap-[6px] items-center capitalize text-sm text-yellow bg-[#FDB6000D]">
                      <ClockYellowIcon />
                      Pending
                    </span>
                  </div>
                )}
              </div>
              <div className="h-[1px] w-full bg-strokePrimary" />
              <div className="flex flex-col items-start gap-4 w-full">
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary">
                    Bank name
                  </div>
                  <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      (scheduledDetails?.bank_name ?? "--")
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary">
                    Routing number
                  </div>
                  <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      (scheduledDetails?.routing_number ?? "--")
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary">
                    Account number
                  </div>
                  <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      (scheduledDetails?.account_number ?? "--")
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary">
                    SWIFT code
                  </div>
                  <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      (orderData?.swiftCode ?? orderData?.swift_code ?? "--")
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="lg:px-6 px-4 py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
              <div className="flex flex-col items-start gap-5">
                <div className="w-full flex justify-between items-center">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    Payment description
                  </div>
                  {isLoading ? (
                    <Skeleton width={120} height={20} />
                  ) : !isScheduled ? (
                    paymentStatus !== "" && (
                      <div className="w-fit ">
                        <span
                          className={`py-[3px] px-[10px] rounded-[6px] flex gap-[6px] items-center text-sm
                      ${
                        paymentStatus === "Succeeded" ||
                        paymentStatus === "Disputed Won"
                          ? "bg-[#23B3390D] text-green"
                          : paymentStatus === "Failed" ||
                              paymentStatus === "Disputed Lost"
                            ? "bg-[#FF00000D] text-red"
                            : paymentStatus === "Voided" ||
                                paymentStatus === "Pending"
                              ? "bg-[#FDB6000D] text-yellow"
                              : paymentStatus === "Refunded" ||
                                  paymentStatus === "Partial refund"
                                ? "bg-[#1678F20D] text-blue"
                                : paymentStatus === "Disputed"
                                  ? " bg-[#4F576A0D] text-iconBright"
                                  : paymentStatus === "Incomplete"
                                    ? "bg-[#4F576A0D] text-iconBright "
                                    : ""
                      }
                    `}
                        >
                          {paymentStatus === "Succeeded" ||
                          paymentStatus === "Disputed Won" ? (
                            <GreenTrueIcon />
                          ) : paymentStatus === "Failed" ||
                            paymentStatus === "Disputed Lost" ? (
                            <RedCrossIcon />
                          ) : paymentStatus === "Refunded" ? (
                            <MoneyReciveIcon />
                          ) : paymentStatus === "Partial refund" ? (
                            <DollarProcessIcon />
                          ) : paymentStatus === "Disputed" ? (
                            <ExclaimIcon2 strokeColor="var(--color-icon-bright)" />
                          ) : paymentStatus === "Pending" ? (
                            <ClockYellowIcon />
                          ) : paymentStatus === "Voided" ? (
                            <CloseYellowIcon />
                          ) : paymentStatus === "Disputed Won" ? (
                            <TickSquare />
                          ) : paymentStatus === "Disputed Lost" ? (
                            <DangerIcon />
                          ) : paymentStatus === "Incomplete" ? (
                            <ExcalimIcon />
                          ) : (
                            ""
                          )}
                          {paymentStatus === "Disputed Won"
                            ? "Succeeded"
                            : paymentStatus === "Disputed Lost"
                              ? "Failed"
                              : paymentStatus}
                        </span>
                      </div>
                    )
                  ) : (
                    <div className="w-fit ">
                      <span
                        className={`py-[3px] px-[10px] rounded-[6px] flex gap-[6px] capitalize items-center text-sm bg-[#FDB6000D] text-yellow`}
                      >
                        <ClockYellowIcon />
                        Pending
                      </span>
                    </div>
                  )}
                </div>
                <div className="h-[1px] w-full bg-strokePrimary" />
                <div className="flex flex-col items-start gap-4 w-full">
                  <div className="flex items-center justify-between w-full">
                    <div className="font-normal text-sm leading-normal text-txtSecondary">
                      Item description
                    </div>
                    <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                      {isLoading ? (
                        <Skeleton width={80} />
                      ) : orderData?.ivr_type === "MANUALPAY" ? (
                        orderData?.payment_note !== "" ? (
                          orderData?.payment_note
                        ) : (
                          "Manual payment"
                        )
                      ) : orderData?.payment_method === "WTO" &&
                        scheduled?.[0]?.status === "Pending" ? (
                        "Pending payment"
                      ) : (
                        orderData?.prodDescription || "Online Payment"
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between w-full">
                    <div className="font-normal text-sm leading-normal text-txtSecondary">
                      Payment date
                    </div>
                    <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                      {isLoading ? (
                        <Skeleton width={150} />
                      ) : (
                        formatPaymentListDate(
                          orderData?.insert_date,
                          orderData?.order_tz ||
                            userData?.data?.accountData?.timezone,
                          "long"
                        )
                      )}
                    </div>
                  </div>

                  {paymentStatus === "Succeeded" &&
                    orderData?.recovered_payment === "Y" && (
                      <div className="flex items-center justify-between w-full">
                        <div className="font-normal text-sm leading-normal text-txtSecondary">
                          Recovered on
                        </div>

                        <div className="font-medium text-sm leading-normal text-txtPrimary text-right cursor-pointer">
                          {formatPaymentListDate(
                            orderData?.last_update,
                            orderData?.order_tz ||
                              userData?.data?.accountData?.timezone,
                            "long"
                          )}
                        </div>
                      </div>
                    )}
                  {paymentStatus === "Failed" && orderData?.error && (
                    <div className="flex items-center justify-between w-full">
                      <div className="font-normal text-sm leading-normal text-txtSecondary">
                        Failed reason
                      </div>
                      <CustomTooltip
                        maxwidth="430px"
                        place="top"
                        id="failed-reason-tooltip"
                        content={`<div class="text-xs leading-[18px] font-normal">${orderData.error}</div>`}
                      >
                        <div
                          id="failed-reason-tooltip"
                          className="font-medium text-sm leading-normal text-txtPrimary text-right cursor-pointer"
                        >
                          {orderData.error.slice(0, 17) + "..."}
                        </div>
                      </CustomTooltip>
                    </div>
                  )}
                  {(paymentStatus === "Refunded" ||
                    paymentStatus === "Partial refund" ||
                    paymentStatus === "Voided" ||
                    paymentStatus === "Disputed") && (
                    <div className="flex items-center justify-between w-full">
                      <div className="font-normal text-sm leading-normal text-txtSecondary">
                        {paymentStatus === "Voided"
                          ? "Voided on"
                          : paymentStatus === "Disputed"
                            ? "Disputed on"
                            : "Refunded on"}
                      </div>
                      <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                        {isLoading ? (
                          <Skeleton width={150} />
                        ) : (
                          formatPaymentListDate(
                            paymentStatus === "Voided"
                              ? orderData?.voidDate || orderData?.last_update
                              : paymentStatus === "Disputed"
                                ? orderData?.dispute_date
                                : orderData?.refundDate,
                            orderData?.order_tz ||
                              userData?.data?.accountData?.timezone,
                            "long"
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="h-[1px] w-full bg-strokePrimary" />
                <div className="flex flex-col items-start gap-4 w-full">
                  {orderData?.payment_method === "WTO" &&
                  orderData?.ivr_type !== "MANUALPAY" ? (
                    <>
                      <div className="flex items-center justify-between w-full">
                        <div className="text-sm leading-normal text-txtPrimary font-medium whitespace-nowrap">
                          Total
                        </div>
                        <div className="font-bold text-sm leading-[150%] text-txtPrimary text-right max-w-[130px]">
                          {isLoading ? (
                            <Skeleton width={80} />
                          ) : (
                            formatCurrency(scheduledDetails?.amount) || "$0.00"
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between w-full">
                      <div className="text-sm leading-normal text-txtPrimary font-medium whitespace-nowrap">
                        Amount
                      </div>
                      <div className="font-bold text-sm leading-[150%] text-txtPrimary text-right max-w-[130px]">
                        {isLoading ? (
                          <Skeleton width={80} />
                        ) : (
                          formatCurrency(orderData?.amount) || "$0.00"
                        )}
                      </div>
                    </div>
                  )}
                  {orderData?.cc_fee_amount !== 0 ||
                  orderData.ach_fee_amount !== 0 ? (
                    <div className="flex items-center justify-between w-full">
                      <div className="text-sm leading-normal text-txtPrimary font-medium whitespace-nowrap">
                        {isLoading ? (
                          <Skeleton width={120} />
                        ) : (
                          <>
                            {orderData?.isStax
                              ? "Surcharge"
                              : portalSettings?.cc_fee_label
                                ? portalSettings?.cc_fee_label
                                : "Convenience fee"}
                          </>
                        )}
                      </div>
                      <div className="font-bold text-sm leading-[150%] text-txtPrimary text-right max-w-[130px]">
                        {isLoading ? (
                          <Skeleton width={80} />
                        ) : (
                          formatCurrency(
                            orderData?.ach_fee_amount === 0
                              ? orderData?.cc_fee_amount
                              : orderData?.ach_fee_amount
                          ) || "$0.00"
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="lg:px-6 px-4 py-5 bg-btnPrimaryActive border-x border-b border-strokePrimary rounded-b-lg flex flex-col gap-[10px]">
              {orderData?.payment_method === "WTO" &&
              orderData?.ivr_type !== "MANUALPAY" ? (
                <>
                  <div className="flex items-center justify-between w-full">
                    <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                      Amount paid
                    </div>
                    <div className="flex items-center gap-2 text-bgPrimary font-bold">
                      {isLoading ? (
                        <Skeleton width={100} />
                      ) : paymentStatus === "Failed" ? (
                        "($0.00)"
                      ) : (
                        `(${formatCurrency(orderData?.amount || 0)})`
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between w-full">
                    <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                      Amount remaining{" "}
                    </div>
                    <div className="flex items-center gap-2 text-bgPrimary font-bold">
                      {isLoading ? (
                        <Skeleton width={100} />
                      ) : paymentStatus === "Failed" ? (
                        "($0.00)"
                      ) : (
                        (() => {
                          const result =
                            (scheduledDetails?.amount || 0) -
                            (orderData?.amount || 0);
                          return result < 0
                            ? `($${Math.abs(result).toFixed(2)})`
                            : `$${result.toFixed(2)}`;
                        })()
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between w-full">
                    <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                      {isScheduled ? "Total Pending" : "Total Paid"}
                    </div>
                    <div className="flex items-center gap-2 text-bgPrimary font-bold">
                      {isLoading ? (
                        <Skeleton width={100} />
                      ) : paymentStatus === "Failed" ||
                        paymentStatus === "Incomplete" ? (
                        "$0.00"
                      ) : (
                        formatCurrency(
                          orderData?.amount +
                            (orderData?.ach_fee_amount === 0
                              ? orderData?.cc_fee_amount
                              : orderData?.ach_fee_amount)
                        ) || "$0.00"
                      )}
                    </div>
                  </div>
                  {!isLoading &&
                  (paymentStatus === "Refunded" ||
                    paymentStatus === "Partial refund" ||
                    paymentStatus === "Voided" ||
                    paymentStatus === "Disputed") ? (
                    <div className="flex items-start justify-between font-bold uppercase text-bgprimary">
                      <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                        {paymentStatus === "Voided"
                          ? "Voided"
                          : paymentStatus === "Disputed"
                            ? "Disputed"
                            : "Refunded"}
                      </div>
                      <div className="flex items-center gap-2 text-bgPrimary font-bold">
                        {!orderData?.refundAmount
                          ? `(${formatCurrency(
                              orderData?.amount +
                                (orderData?.ach_fee_amount === 0
                                  ? orderData?.cc_fee_amount
                                  : orderData?.ach_fee_amount)
                            )})` || "$0.00"
                          : `(${formatCurrency(orderData?.refundAmount)})` ||
                            "($0.00)"}
                      </div>
                    </div>
                  ) : null}
                  {/* {!isLoading && paymentStatus === "Disputed Won" && (
                    <div className="flex items-start justify-between font-bold uppercase text-bgprimary">
                      <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                        Final disputed amount
                      </div>
                      <div className="flex items-center gap-2 text-bgPrimary font-bold">
                        {formatCurrency(
                          (orderData?.amount || 0) +
                            (orderData?.ach_fee_amount === 0
                              ? orderData?.cc_fee_amount || 0
                              : orderData?.ach_fee_amount || 0)
                        ) || "$0.00"}
                      </div>
                    </div>
                  )} */}
                  {!isLoading &&
                  (paymentStatus === "Refunded" ||
                    paymentStatus === "Partial refund" ||
                    paymentStatus === "Voided" ||
                    paymentStatus === "Disputed") ? (
                    <div className="flex items-start justify-between font-bold uppercase text-bgprimary">
                      <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                        Adjusted total
                      </div>
                      <div className="flex items-center gap-2 text-bgPrimary font-bold">
                        {formatCurrency(
                          // paymentStatus === "Disputed Won"
                          //   ? (orderData?.amount ?? 0) +
                          //       ((orderData?.ach_fee_amount ?? 0) === 0
                          //         ? (orderData?.cc_fee_amount ?? 0)
                          //         : (orderData?.ach_fee_amount ?? 0))
                          //   :
                          paymentStatus === "Partial refund" ||
                            paymentStatus === "Refunded"
                            ? (orderData?.amount ?? 0) +
                                ((orderData?.ach_fee_amount ?? 0) === 0
                                  ? (orderData?.cc_fee_amount ?? 0)
                                  : (orderData?.ach_fee_amount ?? 0)) -
                                (orderData?.refundAmount ?? 0)
                            : // : paymentStatus === "Disputed Lost" ||
                              paymentStatus === "Voided"
                              ? 0
                              : (orderData?.refundAmount ?? 0)
                        )}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
      </div>
      {isScheduledArray ? (
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="flex flex-col items-start gap-4 w-full">
            <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
              <div className="w-full flex flex-col items-start gap-5 overflow-x-scroll">
                <div className="flex items-center gap-x-2 justify-between w-full lg:flex-nowrap flex-wrap">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    SCHEDULE
                  </div>
                  <div className="flex items-center gap-[10px] lg:w-fit w-full lg:justify-start justify-between lg:pt-0 pt-3 ">
                    <p className="text-xs leading-normal text-txtParagraph">
                      Frequency:{" "}
                      <span className="font-medium text-txtPrimary">
                        {changePaymentScheduleName(
                          scheduledDetails?.payment_schedule
                        ) || "--"}
                      </span>
                    </p>
                    <div className="w-[1px] h-[19px] bg-strokePrimary lg:block hidden" />
                    <p className="text-xs leading-normal text-txtParagraph">
                      Remaining payments:{" "}
                      <span className="font-medium text-txtPrimary">
                        {scheduledDetails?.remaining_payments || "--"}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary  min-w-[384px]" />
                <div
                  className={`grid ${orderData?.c_FeeAmount ? "grid-cols-4" : "grid-cols-3"} items-center w-full min-w-[384px]`}
                >
                  <div
                    className={` text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Payment Date
                  </div>
                  <div
                    className={` text-sm text-txtParagraph leading-normal text-center`}
                  >
                    Status
                  </div>
                  {orderData?.c_FeeAmount ? (
                    <div
                      className={` text-sm text-txtParagraph leading-normal text-center`}
                    >
                      {orderData?.isStax ? "Surcharge" : "Fee"}
                    </div>
                  ) : null}
                  <div
                    className={` text-sm text-txtParagraph leading-normal text-end`}
                  >
                    Amount
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary  min-w-[384px]" />
                {scheduled?.length
                  ? scheduled?.map((x: any) => {
                      const scheduledRowId =
                        x?.id ||
                        x?.order_id ||
                        x?.custom_schedule_id ||
                        x?.payment_date ||
                        x?.billing_date;
                      return (
                        <div
                          className={`grid ${orderData?.c_FeeAmount ? "grid-cols-4" : "grid-cols-3"} items-center w-full min-w-[384px]`}
                          key={scheduledRowId}
                          data-testid={tid(
                            `payment-preview-installment-${scheduledRowId || "unknown"}`
                          )}
                        >
                          <div
                            className={` text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                          >
                            {x?.payment_date
                              ? moment(x?.payment_date).format("MM/DD/YYYY")
                              : x?.billing_date
                                ? moment(x?.billing_date).format("MM/DD/YYYY")
                                : "--"}
                          </div>
                          <div
                            className={` text-sm text-txtPrimary leading-normal flex justify-center gap-1.5`}
                          >
                            <div
                              className={`px-2 rounded-md py-[3px] w-max ${
                                x.status === "Pending"
                                  ? "bg-[#FDB6000C] text-yellow"
                                  : x.status === "Approved"
                                    ? "bg-[#1678F20D] text-btnPrimaryActive"
                                    : x.status === "Declined" ||
                                        x.status === "Cancelled" ||
                                        x.status?.toLowerCase() ===
                                          "disputed lost"
                                      ? "bg-[#FF00000D] text-red"
                                      : x.status === "Voided"
                                        ? "bg-[#FDB6000D] text-yellow"
                                        : x.status === "Succeeded" ||
                                            x.status?.toLowerCase() ===
                                              "disputed won"
                                          ? "bg-[#23B3390D] text-green"
                                          : x.status === "RefundedPartial" ||
                                              x.status === "Refunded"
                                            ? "bg-[#1678F20D] text-blue"
                                            : x.status === "Disputed"
                                              ? "bg-[#4F576A0D] text-iconBright"
                                              : ""
                              }`}
                            >
                              {x.status &&
                              (x.status === "Declined" ||
                                x.status === "Cancelled")
                                ? "Failed"
                                : x.status === "RefundedPartial"
                                  ? "Partial refund"
                                  : x?.status}
                            </div>
                            {x.status &&
                              (x.status === "Declined" ||
                                x.status === "Cancelled") && (
                                <Button
                                  text="Pay"
                                  data-testid={tid(
                                    `payment-preview-installment-${scheduledRowId || "unknown"}-pay-button`
                                  )}
                                  aria-label={`Pay failed installment ${x?.order_id || scheduledRowId || ""}`}
                                  className="cursor-pointer w-fit !py-[3px] !px-[8px] !text-xs  leading-6 font-medium "
                                  onClick={() => {
                                    if (!x?.order_id) return;
                                    let encodeString = encode(
                                      `${x?.order_id}_${
                                        orderData?.clusterId
                                      }_${new Date().getTime()}`,
                                      "pid_"
                                    );
                                    setFailedPayment(true);
                                    setFailedPaymentId(encodeString);
                                  }}
                                />
                              )}
                          </div>
                          {orderData?.c_FeeAmount ? (
                            <div
                              className={`  text-sm text-txtPrimary leading-normal text-center`}
                            >
                              {x?.cf_amount
                                ? formatCurrency(x?.cf_amount)
                                : "$0.00"}
                            </div>
                          ) : null}
                          <div
                            className={`  text-sm text-txtPrimary leading-normal text-end`}
                          >
                            {x?.amount ? formatCurrency(x?.amount) : "$0.00"}
                          </div>
                        </div>
                      );
                    })
                  : null}
                <div className="h-[1px] w-full bg-strokePrimary  min-w-[384px]" />
                <div
                  className={`grid ${orderData?.c_FeeAmount ? "grid-cols-4" : "grid-cols-3"} items-center w-full min-w-[384px]`}
                >
                  <div
                    className={`w-full  text-xs leading-normal text-txtPrimary`}
                  >
                    SUBTOTAL
                  </div>
                  <div className={`w-full `}></div>
                  {orderData?.c_FeeAmount ? (
                    <div
                      className={`w-full min-w-[56px] text-center text-txtPrimary font-bold leading-normal`}
                    >
                      {formatCurrency(scheduledTotals.fee) || "$0.00"}
                    </div>
                  ) : null}
                  <div
                    className={` text-end text-txtPrimary font-bold leading-normal`}
                  >
                    {formatCurrency(scheduledTotals.amount) || "$0.00"}
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[384px]" />
                <div className="flex items-center justify-between w-full min-w-[384px]">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary min-w-[152px]">
                    Total
                  </div>
                  <div className="text-lg leading-normal font-bold text-txtPrimary  min-w-[192px] text-end">
                    {formatCurrency(
                      scheduledTotals.amount + scheduledTotals.fee
                    ) || "$0.00"}
                  </div>
                </div>
              </div>
            </div>
            {orderData?.payment_method !== "PP" &&
              Number(orderData?.totalconveFeeAmount) === 0 && (
                <div className="flex items-center gap-2 text-xs font-normal leading-normal text-txtParagraph ">
                  <FilledInfoIcon />
                  <p>
                    A{" "}
                    <span className="lowercase">
                      {portalSettings?.cc_fee_label
                        ? portalSettings?.cc_fee_label
                        : "convenience fee"}
                    </span>{" "}
                    is added to each installment payment.
                  </p>
                </div>
              )}
          </div>
        </div>
      ) : null}

      {!isLoading && (
        <div className={`py-5 lg:px-10 px-5 bottom-0 flex justify-center `}>
          <QuestionsFooter isPreview={true} />
        </div>
      )}
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={failedPayment}
        size={`${isLessThan680 ? `100vw` : "680px"}`}
        onClose={() => {
          setFailedPayment(false);
          setFailedPaymentId(null);
        }}
        overlayColor="#000"
      >
        <div
          className="contents"
          data-testid={tid("payment-preview-failed-payment-drawer")}
        >
          <FailedPayment
            id={failedPaymentId}
            show={failedPayment}
            setShow={setFailedPayment}
            setId={setFailedPaymentId}
            fetchDetails={() => onGetPaymentReceipt()}
          />
        </div>
      </Drawer>
    </div>
  );
};

export default PaymentPreview;
